import React from "react";
import DropDown from "../../../../../common/components/shared/dropDown/dropDown";
import Row from "../../../../../common/components/shared/row/row";
import {storesContext} from "../../../../../common/stores/stores";
import i18n from "../../../../../i18n/i18n";
import TextInput from "../../../../../common/components/shared/textInput/textInput";
import {IOrderStore} from "../../../../stores/orderStore";
import {observer} from "mobx-react";
import CargoTypeDropdownItems from "../../../../../helpers/cargoTypeDropdownItems";
import RelationTypeDropdownItems from "../../../../../helpers/relationTypeDropdownItems";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../../common/models/httpError";
import Button from "../../../../../common/components/shared/button/button";
import ChangeStatusPopup from "./changeStatusPopup/changeStatusPopup";
import {ValidatedValue} from "../../../../../helpers/validatedValue";
import {SupervisorFlowAction} from "../../../../models/order";
import Validator from "../../../../../helpers/validator";
import {errorMessages} from "../../../../../helpers/errorMessage";
import {OrderService} from "../../../../services/orderService";
import {Api} from "../../../../../helpers/api";
import {TransportationService} from "../../../../services/transportationService";

interface GeneralInformationSectionProps {
    validForm: boolean;
    isUpdate: boolean;
    orderId?: string;
}

interface GeneralInformationSectionState {
    availableAction: AxiosResponse<HttpErrorModel[] | any> | undefined;
    changeStatusResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    displayPopup: "changeStatus" | null;
    selectedAction: ValidatedValue<SupervisorFlowAction | null>;
    isChangeStatusWaiting: boolean;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

class GeneralInformationSection extends React.Component <GeneralInformationSectionProps, GeneralInformationSectionState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            availableAction: undefined,
            changeStatusResponse: undefined,
            displayPopup: null,
            selectedAction: new ValidatedValue(null, [notEmptyValidator]),
            isChangeStatusWaiting: false
        };
    }

    async componentDidMount() {
        const orderStore = this.context.orderStore as IOrderStore;

        // Clearing the relation Type when entering the form of adding a new order (previously orderStore is used to hide tabs and may not be empty)
        !this.props.isUpdate && orderStore.relationType.setValue("");
    }

    render() {
        const orderStore = this.context.orderStore as IOrderStore;
        const currentStatus = new ValidatedValue(i18n.t(`orderStatus.${orderStore.status.value}`));
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.general.header")}
                </div>
                {this.state.displayPopup === "changeStatus" && this.props.orderId && this.renderChangeStatusPopup()}
                <div className="section-content">
                    <Row class="flex-type gap-30">
                        <div className="status-section">
                            <TextInput
                                label={i18n.t("transportation.order.details.general.properties.status")}
                                validatedValue={currentStatus}
                                testId="status"
                                disabled
                                className={"status-input"}
                            />
                            {this.props.isUpdate && this.renderChangeStatusBtn()}
                        </div>
                        <DropDown
                            className="element"
                            label={i18n.t("transportation.order.details.general.properties.relationType")}
                            items={RelationTypeDropdownItems.getRelationTypesWithoutTicket()}
                            validatedValue={orderStore.relationType}
                            testId="relation-type"
                            onChange={async () => {
                                if (this.props.validForm) {
                                    orderStore.orderIsValid();
                                }
                            }}
                            withoutSort
                        />
                        <DropDown
                            className="element"
                            label={i18n.t("transportation.order.details.general.properties.cargoType")}
                            items={CargoTypeDropdownItems.getCargoTypeDropdownItems()}
                            validatedValue={orderStore.cargoType}
                            testId="cargo-type"
                            onChange={async () => {
                                orderStore.addDangerousGoodsValidation();

                                if (this.props.validForm) {
                                    orderStore.orderIsValid();
                                }
                            }}
                        />
                        {orderStore.displayPinCode && (
                            <TextInput
                                label={i18n.t("transportation.order.details.general.properties.pinCode")}
                                validatedValue={orderStore.pinCode}
                                testId="pin-code"
                                offAutocomplete
                            />
                        )}
                        {orderStore.displayBarCode && (
                            <TextInput
                                label={i18n.t("transportation.order.details.general.properties.barcodeNo")}
                                validatedValue={orderStore.barcodeNo}
                                testId="barcode-no"
                                offAutocomplete
                            />
                        )}
                    </Row>
                </div>
            </div>
        );
    }


    private renderChangeStatusPopup() {
        return (
            <ChangeStatusPopup
                displayPopup
                closePopup={this.closeStatusPopup.bind(this)}
                confirm={this.changeStatus.bind(this)}
                response={this.state.changeStatusResponse}
                orderId={this.props.orderId!}
                isWaiting={this.state.isChangeStatusWaiting}
                selectedAction={this.state.selectedAction}
                onChangeDropdown={(newValue: SupervisorFlowAction) => {
                    this.state.selectedAction.setValue(newValue);
                }}
            />
        );
    }

    private async changeStatus() {
        this.state.selectedAction.validate();
        const orderStore = this.context.orderStore as IOrderStore;
        if (this.state.selectedAction.value && this.props.orderId) {
            this.setState({
                isChangeStatusWaiting: true
            });
            const response = await TransportationService.executeSupervisorAction(this.props.orderId, this.state.selectedAction.value);
            this.setState({
                changeStatusResponse: response,
            });
            if (Api.isSuccess(response)) {
                const order = await OrderService.getOrder(this.props.orderId);
                orderStore.status.setValue(order.data.status);
                this.closeStatusPopup();
            }
            this.setState({
                isChangeStatusWaiting: false
            });
        }
    }

    private closeStatusPopup() {
        this.setState({
            displayPopup: null,
            availableAction: undefined,
            changeStatusResponse: undefined,
        });
        this.state.selectedAction.setValue(null);
    }

    private renderChangeStatusBtn() {
        return (
            <Button
                testId="change-status-button"
                variant={"text"}
                className="change-status-button"
                type="secondary"
                text={i18n.t("transportation.order.actions.modifyStatus")}
                onClick={() => {
                    this.setState({displayPopup: "changeStatus"});
                }}
            />
        );
    }
}

export default observer(GeneralInformationSection);
